/**
 * ServiceDeskDashboardPage — `/service-desk` index landing.
 *
 * Slice B10: wired to GET /v1/service-desk/dashboard — real KPI data.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Clock, ExternalLink, FileText, Send } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { getServiceDeskDashboard } from '../../lib/api/service-desk';
import { PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function ServiceDeskDashboardPage(): JSX.Element {
  const { data, isError, error, isPending } = useQuery({
    queryKey: ['service-desk', 'dashboard'],
    queryFn: () => getServiceDeskDashboard(),
    retry: 1,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Service desk for third parties"
        description="Form templates, applications, MDA notification editor."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/service-desk/forms"
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark inline-flex items-center gap-2"
            >
              Form builder <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/service-desk/applications"
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50 inline-flex items-center gap-2"
            >
              Applications <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        }
      />

      {isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Failed to load dashboard: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Active forms"
          value={isPending ? '…' : String(data?.activeForms ?? 0)}
          hint="Form definitions created by admins"
          icon={FileText}
          tone="default"
        />
        <KPICard
          title="Total submissions"
          value={isPending ? '…' : String(data?.totalSubmissions ?? 0)}
          hint="All service desk cases"
          icon={Send}
          tone="default"
        />
        <KPICard
          title="Pending review"
          value={isPending ? '…' : String(data?.pendingReview ?? 0)}
          hint="Open + in-progress + awaiting"
          icon={Clock}
          tone={data?.pendingReview ? 'warning' : 'default'}
        />
        <KPICard
          title="Partner agencies"
          value={isPending ? '…' : String(data?.partnerAgencies ?? 0)}
          hint="Distinct agencies in catalog"
          icon={Building2}
          tone="default"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {(
          [
            { label: 'Form builder', to: '/service-desk/forms', description: 'Create and manage form definitions' },
            { label: 'Notifications', to: '/service-desk/notifications', description: 'MDA notification templates' },
            { label: 'Data export', to: '/service-desk/data-export', description: 'Export applications and responses' },
            { label: 'Help center', to: '/service-desk/help-center', description: 'Published help articles' },
            { label: 'Applications', to: '/service-desk/applications', description: 'Review form submissions' },
          ] as const
        ).map((tile) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="bg-white border border-border rounded-xl p-4 hover:border-nipost-green/40 hover:bg-nipost-green-light transition-colors"
          >
            <p className="text-sm font-semibold text-gray-900">{tile.label}</p>
            <p className="text-xs text-muted mt-0.5">{tile.description}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
